import { Capacitor } from '@capacitor/core';

/**
 * Native bridge. Everything here is safe to import on the web — the Capacitor
 * plugins detect the platform and the actual native calls are guarded by
 * `isNative()`, so on a normal browser build this module is effectively inert.
 *
 * Usage (main.tsx):
 *   import { initNative } from '@/lib/native';
 *   initNative({ onDeepLink: (path) => router.navigate(path) });
 *
 * After login (authStore.login):
 *   import { registerPushToken } from '@/lib/native';
 *   registerPushToken();
 */

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

interface InitOptions {
  /** Navigate the SPA router to an in-app path (used for deep links). */
  onDeepLink?: (path: string) => void;
}

let initialized = false;

/**
 * One-time native startup. Hides the splash, themes the status bar, wires the
 * Android hardware back button to the SPA history, and routes deep links.
 * No-ops entirely on web.
 */
export async function initNative(opts: InitOptions = {}): Promise<void> {
  if (!isNative() || initialized) return;
  initialized = true;

  // Lazy-import so the web bundle never pulls these in at module-eval time.
  const [{ SplashScreen }, { StatusBar, Style }, { App }] = await Promise.all([
    import('@capacitor/splash-screen'),
    import('@capacitor/status-bar'),
    import('@capacitor/app'),
  ]);

  // Status bar: brand blue with light icons.
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (platform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#111111' });
    }
  } catch {
    /* status bar not critical */
  }

  // Android hardware back: go back in SPA history; if at the root, minimize
  // the app rather than killing it (matches user expectation on Android).
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack || window.history.length > 1) {
      window.history.back();
    } else {
      App.minimizeApp();
    }
  });

  // Deep links — e.g. https://esyprints.com/orders/EP-123 or the Razorpay
  // return URL. Strip the origin and hand the path to the router.
  App.addListener('appUrlOpen', (event) => {
    try {
      const url = new URL(event.url);
      const path = url.pathname + url.search + url.hash;
      if (path && opts.onDeepLink) opts.onDeepLink(path);
    } catch {
      /* malformed deep link — ignore */
    }
  });

  // Hide the splash once the first frame is painted.
  try {
    await SplashScreen.hide();
  } catch {
    /* already hidden */
  }
}

/**
 * Native share sheet with a graceful web fallback (navigator.share, then
 * clipboard). Returns true if something handled the share.
 */
export async function nativeShare(opts: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<boolean> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title: opts.title,
        text: opts.text,
        url: opts.url,
        dialogTitle: opts.title,
      });
      return true;
    } catch {
      return false;
    }
  }

  // Web fallback
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: opts.title, text: opts.text, url: opts.url });
      return true;
    } catch {
      return false;
    }
  }
  if (opts.url && typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(opts.url);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export { registerPushToken, removePushToken } from './push';
